import React from 'react';
import {
  Search,
  Globe,
  Compass,
  ArrowUpDown,
  Plus,
  Bell,
  RefreshCw,
  X,
  Check,
  Telescope,
  LayoutGrid,
  LibraryBig,
} from 'lucide-react';
import { RealtimeStatus, RealtimeSyncStatus } from './RealtimeStatus';

interface HeaderProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  showSearchBar: boolean;
  onToggleSearchBar: () => void;
  isSortMode: boolean;
  onToggleSortMode: () => void;
  onOpenAddModal: () => void;
  onOpenUpdatedToday: () => void;
  onOpenSiteManager: () => void;
  onOpenDownloadLibrary: () => void;
  syncStatus: RealtimeSyncStatus;
  onSyncNow: () => void;
  view: 'tier' | 'discover';
  onChangeView: (view: 'tier' | 'discover') => void;
  updatedTodayCount: number;
  onAutoDiscoverAll: () => void;
  isAutoDiscoveringAll: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  searchQuery,
  onSearchChange,
  showSearchBar,
  onToggleSearchBar,
  isSortMode,
  onToggleSortMode,
  onOpenAddModal,
  onOpenUpdatedToday,
  onOpenSiteManager,
  onOpenDownloadLibrary,
  syncStatus,
  onSyncNow,
  view,
  onChangeView,
  updatedTodayCount,
  onAutoDiscoverAll,
  isAutoDiscoveringAll,
}) => {
  const searchActive = showSearchBar || Boolean(searchQuery);

  return (
    <header className="sticky top-0 z-40 bg-canvas/80 backdrop-blur-xl border-b border-white/[0.06] safe-area-top">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-4">
        {/* Brand */}
        <div className="flex items-center gap-2.5 min-w-0 sm:shrink-0">
          <div className="relative w-8 h-8 shrink-0">
            {/* Glow: red/violet halo plus a blurred copy of the logo for its own colours */}
            <div
              aria-hidden
              className="absolute inset-0 rounded-lg bg-gradient-to-br from-red-500 via-rose-600 to-violet-600 blur-sm opacity-35"
            />
            <img
              src="/logo.png?v=3"
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full rounded-lg object-cover blur-sm opacity-40 saturate-150"
            />
            <img
              src="/logo.png?v=3"
              alt=""
              className="relative w-8 h-8 rounded-lg object-cover ring-1 ring-white/10 shadow-[0_0_6px_rgba(239,68,68,0.2)]"
            />
          </div>
          <h1 className="text-[15px] font-semibold tracking-tight text-zinc-50 truncate">
            Manga Tier Tracker
          </h1>
        </div>

        {/* Tier board / Discover (phones switch from the bottom nav) */}
        <nav className="hidden sm:flex segmented shrink-0">
          {(
            [
              { id: 'tier', label: 'จัดแรงค์', Icon: LayoutGrid, title: 'หน้าจัดแรงค์' },
              { id: 'discover', label: 'สำรวจ', Icon: Telescope, title: 'สำรวจและค้นหามังงะจากเว็บ' },
            ] as const
          ).map(({ id, label, Icon, title }) => (
            <button
              key={id}
              onClick={() => onChangeView(id)}
              title={title}
              aria-pressed={view === id}
              className={`segmented-item h-7 ${view === id ? 'segmented-item-on' : ''}`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        {/* Inline search on wide screens */}
        {view === 'tier' && (
          <div className="hidden lg:flex relative items-center w-full max-w-[16rem] min-w-0 ml-auto">
            <Search className="absolute left-3 w-4 h-4 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="ค้นหาเรื่องในลิสต์"
              className="field h-9 pl-9 pr-8 py-0"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                title="ล้างคำค้นหา"
                className="absolute right-1.5 p-1 rounded-md text-zinc-500 hover:text-zinc-100"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Actions */}
        <div className={`flex items-center gap-0.5 sm:gap-1 shrink-0 ml-auto ${view === 'tier' ? 'lg:ml-0' : ''}`}>
          {view === 'tier' && (
            <button
              onClick={() => {
                if (showSearchBar && searchQuery) {
                  onSearchChange('');
                } else {
                  onToggleSearchBar();
                }
              }}
              title={showSearchBar && searchQuery ? 'ล้างคำค้นหา' : 'ค้นหามังงะ'}
              className={`icon-btn lg:hidden ${searchActive ? 'bg-white/10 text-zinc-50' : ''}`}
            >
              <Search className="w-[18px] h-[18px]" />
            </button>
          )}

          <button onClick={onOpenSiteManager} title="จัดการเว็บค้นหา" className="icon-btn">
            <Globe className="w-[18px] h-[18px]" />
          </button>

          {/* Today and add live in the bottom nav on phones; sort stays up here (the library goes down there) */}
          {view === 'tier' &&
            (isSortMode ? (
              <>
                <button
                  onClick={onToggleSortMode}
                  title="บันทึกการจัดเรียง"
                  className="hidden sm:inline-flex btn btn-sm bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
                >
                  <Check className="w-4 h-4" />
                  <span>เสร็จสิ้น</span>
                </button>
                <button
                  onClick={onToggleSortMode}
                  title="บันทึกการจัดเรียง"
                  className="sm:hidden icon-btn bg-emerald-500/15 text-emerald-400 hover:text-emerald-300"
                >
                  <Check className="w-[18px] h-[18px]" />
                </button>
              </>
            ) : (
              <button
                onClick={onToggleSortMode}
                title="เปิดโหมดจัดเรียงการ์ด"
                className="icon-btn"
              >
                <ArrowUpDown className="w-[18px] h-[18px]" />
              </button>
            ))}

          <button
            onClick={onOpenDownloadLibrary}
            title="คลังดาวน์โหลด อ่านต่อจากที่ค้างไว้"
            className="hidden sm:inline-flex icon-btn"
          >
            <LibraryBig className="w-[18px] h-[18px]" />
          </button>

          {view === 'tier' && (
            <button
              onClick={onAutoDiscoverAll}
              disabled={isAutoDiscoveringAll || syncStatus.phase !== 'idle'}
              title={isAutoDiscoveringAll ? 'กำลังค้นหาเว็บ...' : 'ค้นหาเว็บอัตโนมัติสำหรับทุกเรื่องในระบบ'}
              className="icon-btn"
            >
              {isAutoDiscoveringAll ? (
                <RefreshCw className="w-[18px] h-[18px] animate-spin" />
              ) : (
                <Compass className="w-[18px] h-[18px]" />
              )}
            </button>
          )}

          <button
            onClick={onOpenUpdatedToday}
            title="ดูมังงะที่อัปเดตวันนี้"
            className="hidden sm:inline-flex icon-btn relative"
          >
            <Bell className="w-[18px] h-[18px]" />
            {updatedTodayCount > 0 && (
              <span className="absolute top-1 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold leading-4 text-center tabular-nums">
                {updatedTodayCount}
              </span>
            )}
          </button>

          {/* Realtime chapter tracking status */}
          <RealtimeStatus status={syncStatus} onSyncNow={onSyncNow} />

          <span className="hidden sm:block w-px h-5 bg-white/10 mx-1.5" />

          <button onClick={onOpenAddModal} title="เพิ่มมังงะ" className="hidden sm:inline-flex btn btn-primary h-9 px-3">
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">เพิ่มมังงะ</span>
          </button>
        </div>
      </div>

      {/* Expandable search bar (narrow screens) */}
      {showSearchBar && view === 'tier' && (
        <div className="lg:hidden border-t border-white/[0.06] px-3 sm:px-6 py-2.5">
          <div className="max-w-lg mx-auto relative flex items-center">
            <Search className="absolute left-3 w-4 h-4 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="พิมพ์ชื่อเรื่องที่ต้องการค้นหา..."
              className="field h-10 pl-9 pr-10 py-0"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2 p-1.5 rounded-md text-zinc-500 hover:text-zinc-100"
                title="ล้างคำค้นหา"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
